import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CurrenciesFilterParams, CreateCurrencyParams, UpdateCurrencyParams } from '@/types/currency'
import { useCurrenciesStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'
import { SortOrderEnum } from '@/enums'

export const useCurrencies = (params: CurrenciesFilterParams, options?: { enabled?: boolean }) => {
  const fetchCurrencies = useCurrenciesStore(state => state.fetchCurrencies)

  return useQuery({
    queryKey: ['currencies', params],
    queryFn: () => fetchCurrencies(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true
  })
}

export const useCurrency = (id: string) => {
  const getCurrency = useCurrenciesStore(state => state.getCurrency)

  return useQuery({
    queryKey: ['currency', id],
    queryFn: () => getCurrency(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateCurrency = () => {
  const createCurrency = useCurrenciesStore(state => state.createCurrency)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CreateCurrencyParams) => createCurrency(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['exchange-rates'] })
    }
  })
}

export const useUpdateCurrency = () => {
  const updateCurrency = useCurrenciesStore(state => state.updateCurrency)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ currencyId, params }: { currencyId: string; params: UpdateCurrencyParams }) =>
      updateCurrency(currencyId, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['exchange-rates'] })
    }
  })
}

export const useDeleteCurrency = () => {
  const deleteCurrency = useCurrenciesStore(state => state.deleteCurrency)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteCurrency(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['exchange-rates'] })
    }
  })
}

export const useBulkDeleteCurrencies = () => {
  const bulkDeleteCurrencies = useCurrenciesStore(state => state.bulkDeleteCurrencies)
  const deselectAllCurrencies = useCurrenciesStore(state => state.deselectAllCurrencies)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteCurrencies(ids),
    onSuccess: async response => {
      deselectAllCurrencies()
      handleBulkDeleteSuccess(response)
      await queryClient.invalidateQueries({ queryKey: ['exchange-rates'] })
    }
  })
}

export const useSortCurrency = () => {
  const sortCurrency = useCurrenciesStore(state => state.sortCurrency)

  return useMutation({
    mutationFn: ({ currencyId, type }: { currencyId: string; type: SortOrderEnum }) => sortCurrency(currencyId, type),
    onSuccess: () => {}
  })
}
